import { FC, useState, Suspense, useRef } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Toolbar } from 'primereact/toolbar';
import { Skill } from '../../models/Skill';
import { SkillsByType } from '../../models/SingleUnit';
import SingleUnitSupportSkillsContainer from './SingleUnitSupportSkillsContainer';
import SingleUnitDataTable from './SingleUnitDataTable';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { Button } from 'primereact/button';
import { UsersBySkillResults } from '../../models/UsersBySkillResults';
import { progressBarSingleUnit } from '../../businessLogicService/progressBarAssignValues';
import SingleUnitProgressBar from './SingleUnitProgressBar';
import { ProgressBarParent } from '../../models/ProgressBar';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import SingleUnitSkillDialog from './SingleUnitSkillDialog';
import { TieredMenu } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import { SelectAssignmentModeModal } from '../../models/Assignment';
import { AssignmentSidebarOpenModal } from '../AssignmentSidebar/AssignemntSidebarOpenModal/AssignmentSidebarOpenModal';
import { AssignmentOpenModalButton } from '../Assignments/AssignmentOpenModalButton';
import { PresentLessonButton } from '../TeacherLedLesson/PresentLessonButton';
import SingleUnitStandardBadges from './SingleUnitStandardBadges';
import { Divider } from 'primereact/divider';
import { ChevronRight, FilterX } from 'lucide-react';

interface Props {
  skillsInUnit?: SkillsByType[];
  keyProp: number;
  targetSkillsIds: string[];
  selectedStandardIds?: string[];
  /** Drives the "Not filtered" marker above the lists the optional filters deliberately leave alone. */
  optionalFiltersApplied?: boolean;
}

const SingleUnitRightColumn: FC<Props> = ({
  skillsInUnit,
  keyProp,
  targetSkillsIds,
  selectedStandardIds,
  optionalFiltersApplied,
}) => {
  const tieredMenuRef = useRef<TieredMenu>(null);
  const [itemsTieredMenu, setItemsTieredMenu] = useState<MenuItem[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<Skill>();
  const [showCreateAssignmentModel, setCreateAssignmentModal] = useState<SelectAssignmentModeModal>({
    visible: false,
    skill: null,
  });
  const skillsTypeTarget = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.TARGET);
  const skillsTypeSupport = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.SUPPORT);
  const skillsTypeExtension = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.EXTENSION);
  const firstLessonSkillId = skillsTypeTarget?.skills.find(s => s.lessons?.length)?.id;

  const accordionProgressBar = (usersBySkillResults: UsersBySkillResults[]) => {
    const values = progressBarSingleUnit(usersBySkillResults);
    return (
      <SingleUnitProgressBar
        values={values}
        parent={ProgressBarParent.SINGLE_UNIT}
        tieredMenuRef={tieredMenuRef}
        setItemsTieredMenu={setItemsTieredMenu}
        fullWidth={true}
      />
    );
  };

  const accordionStart = (skillValue: Skill) => {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <span className="tw-flex tw-items-center tw-gap-2">
          <i className="nc_icon-nc_target icon-sm nc_unit-skill-type-icon" />
          <h3>
            {skillValue.name}{' '}
            <span className="tw-text-base tw-text-neutral-700 tw-font-normal">({skillValue.code})</span>
          </h3>
        </span>
        <SingleUnitStandardBadges standards={skillValue.standards} selectedStandardIds={selectedStandardIds} />
      </div>
    );
  };

  const accordionCenter = (skillValue: Skill) => {
    return skillValue.lessonNumbers?.length ? (
      <div className="tw-flex tw-flex-col tw-gap-1 tw-pt-[5px]">
        <h6 className="tw-text-neutral-700 tw-font-bold tw-font-atkinson tw-text-xs">Lessons:</h6>
        <h6 className="nc_unit-lessons">{skillValue.lessonNumbers.map(el => el).join(', ')}</h6>
      </div>
    ) : (
      <></>
    );
  };

  const accordionEnd = (skillValue: Skill) => {
    return (
      <>
        <Divider layout="vertical" className="tw-w-[2px]" />
        <div className="tw-flex tw-items-center">
          <div className="tw-items-end">
            <PresentLessonButton
              skill={{ id: skillValue.id, code: skillValue.code, name: skillValue.name }}
              disabled={!skillValue.lessons?.length}
              showCoachmark={skillValue.id === firstLessonSkillId}
            />
          </div>
          <Button
            className="p-button-link"
            label="View"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSkill(skillValue);
              setVisible(true);
            }}
          ></Button>
          <AssignmentOpenModalButton
            setCreateAssignmentModal={setCreateAssignmentModal}
            entryPoint="units"
            skill={{
              id: skillValue.id,
              code: skillValue.code,
              name: skillValue.name,
              targetSkillType: skillValue.targetSkillType,
            }}
          />
        </div>
      </>
    );
  };

  const accordionHeader = (skill: Skill) => {
    return (
      <div className="tw-flex tw-flex-col">
        <Toolbar
          start={() => accordionStart(skill)}
          center={() => accordionCenter(skill)}
          end={() => accordionEnd(skill)}
          className="w-full nc_accordion-header nc_unit-skill"
        />
        <>{skill.usersBySkillResults && accordionProgressBar(skill.usersBySkillResults as UsersBySkillResults[])}</>
      </div>
    );
  };

  const supportAndExtensionCenter = (type: TargetSkillTypeLabel) => {
    const name = type === TargetSkillTypeLabel.SUPPORT ? 'Additional Support' : 'Extension';
    return (
      <span className="tw-flex tw-gap-4 tw-items-center">
        <i className={`nc_icon-nc_${type.toLowerCase()} icon-sm nc_unit-skill-type-icon !tw-text-stone-500`} />
        <h3>{name} Skills</h3>
        {/* Rotated to point down while the tab is open; see nc_unit-section-chevron. */}
        <ChevronRight className="tw-text-neutral-950 nc_unit-section-chevron" />
      </span>
    );
  };

  const supportAndExtensionHeader = (type: TargetSkillTypeLabel) => {
    return (
      <Toolbar center={() => supportAndExtensionCenter(type)} className="w-full nc_accordion-header" role="section" />
    );
  };

  return (
    <>
      <AssignmentSidebarOpenModal
        showCreateAssignmentModel={showCreateAssignmentModel}
        setCreateAssignmentModal={setCreateAssignmentModal}
      />
      {skillsInUnit && (
        <Suspense fallback={<HorizontalLoading />}>
          {/* Tooltip targets are document-wide selectors, so they hang off a marker class rather than the
              icon classes, which the left column's cards and the ancestor skill rows also use. */}
          <Tooltip target=".nc_unit-skill-type-icon.nc_icon-nc_target">Target Skill</Tooltip>
          <Tooltip target=".nc_unit-skill-type-icon.nc_icon-nc_support">Support Skill</Tooltip>
          <Tooltip target=".nc_unit-skill-type-icon.nc_icon-nc_extension">Extension Skill</Tooltip>

          <Accordion className="tw-flex tw-flex-col tw-gap-6 tw-justify-start" key={keyProp}>
            {skillsTypeTarget?.skills.map(skill => {
              return (
                <AccordionTab
                  key={skill.id}
                  header={() => accordionHeader(skill)}
                  className="nc_unit-skill-accordion nc_unit-target-skill"
                >
                  <div className="nc_content-frame tw-flex tw-flex-col tw-gap-lg tw-pt-0">
                    {skill.skillAncestries && (
                      <SingleUnitSupportSkillsContainer
                        skillTypeUnit={skillsTypeTarget.skills}
                        skillAncestries={skill.skillAncestries}
                        tieredMenuRef={tieredMenuRef}
                        setItemsTieredMenu={setItemsTieredMenu}
                        targetSkillsIds={targetSkillsIds}
                        selectedStandardIds={selectedStandardIds}
                        setCreateAssignmentModal={setCreateAssignmentModal}
                      />
                    )}
                  </div>
                </AccordionTab>
              );
            })}
          </Accordion>

          {/* The optional filters only narrow target skills, so the lists below carry a marker saying
              they still show the whole unit. PrimeReact's Accordion drops any child that is not an
              AccordionTab, which is why this sits between two accordions rather than inside one. */}
          {optionalFiltersApplied && (
            <Divider
              align="center"
              pt={{
                content: {
                  className: '!tw-px-0',
                },
              }}
            >
              <span className="tw-flex tw-items-center tw-gap-2 tw-font-atkinson tw-text-xs tw-font-bold tw-text-neutral-700 tw-bg-neutral-50 ">
                <FilterX size={16} aria-hidden="true" className="tw-text-neutral-700" />
                Not filtered
              </span>
            </Divider>
          )}

          <Accordion className="tw-flex tw-flex-col tw-gap-6 tw-justify-start" key={`${keyProp}-unfiltered`}>
            <AccordionTab
              header={() => supportAndExtensionHeader(TargetSkillTypeLabel.SUPPORT)}
              className="nc_unit-skill-accordion nc_unit-skill-accordion-padded nc_unit-additional-support-skill"
            >
              <SingleUnitDataTable
                skills={skillsTypeSupport?.skills as Skill[]}
                tieredMenuRef={tieredMenuRef}
                setItemsTieredMenu={setItemsTieredMenu}
                selectedStandardIds={selectedStandardIds}
              />
            </AccordionTab>

            <AccordionTab
              header={() => supportAndExtensionHeader(TargetSkillTypeLabel.EXTENSION)}
              className="nc_unit-skill-accordion nc_unit-skill-accordion-padded nc_unit-extension-skill"
            >
              <SingleUnitDataTable
                skills={skillsTypeExtension?.skills as Skill[]}
                tieredMenuRef={tieredMenuRef}
                setItemsTieredMenu={setItemsTieredMenu}
                selectedStandardIds={selectedStandardIds}
              />
            </AccordionTab>
          </Accordion>
        </Suspense>
      )}
      {visible && skill && <SingleUnitSkillDialog visible={visible} setVisible={setVisible} skill={skill} />}
      {itemsTieredMenu && tieredMenuRef && (
        <TieredMenu
          appendTo={document.body}
          model={itemsTieredMenu}
          popup
          ref={tieredMenuRef}
          breakpoint="767px"
          className="nc_tieredmenu-fixed-height"
        />
      )}
    </>
  );
};

export default SingleUnitRightColumn;
